import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/orizon-ui/Modal';
import QtyStepper from '../../../components/orizon-ui/QtyStepper';
import { FormSection, ToggleField, SelectField, InfoBox } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useBarcodeCodice } from '../../../components/colata/BarcodeCodice';
import {
  CartellinoAnteprima, AreaStampaCartellini, stampaCartellini,
} from '../../../components/colata/CartellinoStampa';
import { formattaQta } from '../../../components/gru/scorta';
import { colataLista, mapColataError } from '../../../lib/services/colataService';

const MAX_COPIE = 200;

/**
 * Modale "Cartellino": anteprima del cartellino 50 x 70 mm (uspravno) con il codice a barre Code 128 e stampa
 * (solo i cartellini, uno per pagina, con CSS @page 50x70 mm e margini 0).
 */
const CartellinoModal = ({ articolo, onClose }) => {
  const toast = useToast();
  const { svg, errore, caricamento } = useBarcodeCodice(articolo.codice);

  const [copie, setCopie] = useState(1);
  const [mostraSap, setMostraSap] = useState(true);
  const [mostraUbicazione, setMostraUbicazione] = useState(true);
  const [mostraEtichetta, setMostraEtichetta] = useState(false);
  const [mostraMin, setMostraMin] = useState(false);

  const [etichette, setEtichette] = useState([]); // etichette dei componenti collegati
  const [etichettaScelta, setEtichettaScelta] = useState('');
  const [scala] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 560 ? 1 : 1.2));

  // etichette dei componenti collegati all'articolo
  useEffect(() => {
    let annullato = false;
    colataLista('compatibilita', { perPagina: 50, filtri: { articolo_id: articolo.id } })
      .then((r) => {
        if (annullato) return;
        const uniche = [...new Set(r.righe.map((k) => (k.componente_etichetta || '').trim()).filter(Boolean))];
        setEtichette(uniche);
        setEtichettaScelta(uniche[0] ?? '');
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articolo.id]);

  const ubicazione = articolo.ubicazione_codice
    ? `${articolo.magazzino_label ? `${articolo.magazzino_label} · ` : ''}${articolo.ubicazione_codice}`
    : null;

  const dati = useMemo(() => {
    const voci = [];
    if (mostraSap && articolo.codice_sap) voci.push(`SAP ${articolo.codice_sap}`);
    if (mostraUbicazione && ubicazione) voci.push(`UBIC. ${ubicazione}`);
    if (mostraEtichetta && etichettaScelta) voci.push(`ETICH. ${etichettaScelta}`);
    if (mostraMin) {
      voci.push(`MIN ${formattaQta(articolo.qta_min, articolo.unita_decimali)} ${articolo.unita_label}`);
    }
    return { nome: articolo.nome, voci };
  }, [articolo, ubicazione, mostraSap, mostraUbicazione, mostraEtichetta, mostraMin, etichettaScelta]);

  const puoStampare = Boolean(svg) && !errore && !caricamento;

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        titolo={articolo.nome}
        etichetta={`Cartellino · ${articolo.codice || '—'}`}
        larghezza="lg"
        footer={(
          <>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted/80 transition-all border border-border/40"
            >
              Chiudi
            </button>
            <button
              type="button"
              onClick={stampaCartellini}
              disabled={!puoStampare}
              className="flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:opacity-90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              <Icon name="Printer" size={14} />
              Stampa{copie > 1 ? ` (${copie})` : ''}
            </button>
          </>
        )}
      >
        {errore && <InfoBox tono="warn">{errore}</InfoBox>}

        {/* Anteprima */}
        <FormSection titolo="Anteprima 50 × 70 mm" icona="Barcode">
          <div className="flex justify-center p-4 md:p-6 rounded-2xl bg-muted/30 border border-border/30 overflow-auto">
            {caricamento ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground/50">
                <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-widest">Generazione codice a barre...</span>
              </div>
            ) : (
              <CartellinoAnteprima dati={dati} svg={svg} scala={scala} />
            )}
          </div>
        </FormSection>

        {/* Contenuto del cartellino */}
        <FormSection titolo="Contenuto" icona="ListChecks">
          <ToggleField
            label="Codice SAP"
            sublabel={articolo.codice_sap ? articolo.codice_sap : 'non presente nell\'articolo'}
            checked={mostraSap}
            onChange={setMostraSap}
          />
          <ToggleField
            label="Ubicazione"
            sublabel={ubicazione || 'non presente nell\'articolo'}
            checked={mostraUbicazione}
            onChange={setMostraUbicazione}
          />
          <ToggleField
            label="Etichetta del componente"
            sublabel={etichette.length ? (etichettaScelta || undefined) : 'nessun componente collegato con etichetta'}
            checked={mostraEtichetta}
            onChange={setMostraEtichetta}
            disabilitato={etichette.length === 0}
          />
          {mostraEtichetta && etichette.length > 1 && (
            <SelectField
              label="Quale etichetta" name="etichetta" value={etichettaScelta}
              onChange={setEtichettaScelta}
              opzioni={etichette.map((e) => ({ value: e, label: e }))}
              segnaposto="— Seleziona —"
            />
          )}
          <ToggleField
            label="Scorta minima"
            sublabel={`${formattaQta(articolo.qta_min, articolo.unita_decimali)} ${articolo.unita_label}`}
            checked={mostraMin}
            onChange={setMostraMin}
          />
        </FormSection>

        {/* Copie */}
        <FormSection titolo="Numero di copie" icona="Copy">
          <QtyStepper
            nome="copie"
            value={copie}
            onChange={(v) => setCopie(Math.min(MAX_COPIE, Math.max(1, Math.round(v) || 1)))}
            min={1}
            max={MAX_COPIE}
            decimali={0}
            unitaLabel={copie === 1 ? 'cartellino' : 'cartellini'}
          />
          <InfoBox>
            Per stampare: nella finestra di stampa scegli la stampante di etichette, formato carta personalizzato
            50 × 70 mm (uspravno), margini "Nessuno" e scala 100%. Esce un cartellino per pagina.
          </InfoBox>
        </FormSection>
      </Modal>

      {/* Pagine di stampa (nascoste sullo schermo) */}
      {puoStampare && <AreaStampaCartellini dati={dati} svg={svg} copie={copie} />}
    </>
  );
};

export default CartellinoModal;
